<script setup lang="ts">
import Modal from '../Modal/index.vue'
import { deleteAPIKey } from '~~/src/api/api-key'
import type { APIKey } from '~~/src/api/api-key'

defineProps<{
  apiKeys: APIKey[]
  isLoading: boolean
  error: Error | null
  toggleTokenVisibility: (keyId: number) => void
}>()

const emit = defineEmits(['updateKeys'])

const toast = useToast()
const user = useUser()

const mapPermissions = (permissions: number[]): string[] => {
  const permissionMap: Record<number, string> = {
    1: 'All',
    2: 'Read',
    3: 'Write',
  }
  return permissions.map(permission => permissionMap[permission] || 'unknown')
}

const copyToClipboard = (token: string) => {
  navigator.clipboard.writeText(token).then(() => {
    toast.success('Secret Key Copied')
  }).catch(() => {
    toast.error('Failed to copy Secret Key')
  })
}

const truncatedToken = (token: string) => {
  return token.length > 15 ? `${token.slice(0, 15)}...` : token
}

const isModalVisible = ref(false)
const selectedKeyId = ref<number | undefined>(undefined)

const confirmDelete = (id: number) => {
  selectedKeyId.value = id
  isModalVisible.value = true
}

const handleUpdateKeys = () => {
  emit('updateKeys')
}

const handleCloseModal = () => {
  isModalVisible.value = false
  selectedKeyId.value = undefined
}
</script>

<template>
  <div class="overflow-x-auto">
    <table class="min-w-full bg-white border table-fixed">
      <thead>
        <tr>
          <th class="py-2 px-4 border-b text-left">
            Name
          </th>
          <th class="py-2 px-4 border-b text-left">
            Created By
          </th>
          <th class="py-2 px-4 border-b text-left">
            Secret Key
          </th>
          <th class="py-2 px-4 border-b text-left">
            Permissions
          </th>
          <th class="py-2 px-4 border-b text-left">
            Created At
          </th>
          <th class="py-2 px-4 border-b text-left">
            Action
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="isLoading">
          <td colspan="6" class="text-center py-4">
            Loading...
          </td>
        </tr>
        <tr v-if="error">
          <td colspan="6" class="text-center py-4 text-red-500">
            {{ error.message }}
          </td>
        </tr>
        <tr v-for="key in apiKeys" :key="key.id">
          <td class="py-2 px-4 border-b text-left">
            {{ key.name }}
          </td>
          <td class="py-2 px-4 border-b text-left">
            {{ user?.name }}
          </td>
          <td class="py-2 px-4 border-b text-left">
            <span class="truncate">{{ truncatedToken(key.token) }}</span>
          </td>
          <td class="py-2 px-4 border-b text-left">
            {{ mapPermissions(key.permissions).join(', ') }}
          </td>
          <td class="py-2 px-4 border-b text-left">
            {{ formatUSDate(new Date(key.created_at)) }}
          </td>
          <td class="py-2 px-4 border-b text-left">
            <button class="color-dark-gray hover:color-darker-gray underline mr-1.5" @click="copyToClipboard(key.token)">
              <div i-mdi:content-copy class="m-1.5" />
            </button>
            <button class="color-dark-gray hover:color-darker-gray underline" @click="confirmDelete(key.id)">
              <div i-mdi:delete class="m-1.5" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <Modal
    :is-visible="isModalVisible"
    :selected-key-id="selectedKeyId"
    title="Confirm Deletion"
    message="Are you sure you want to delete this API Key?"
    :delete-function="deleteAPIKey"
    @update-keys="handleUpdateKeys"
    @close-modal="handleCloseModal"
  />
</template>

<style scoped>
.fixed {
  position: fixed;
  z-index: 9999;
}
</style>
